'use client';
import React, { useState } from 'react';

interface Mission {
  id: string;
  emoji: string;
  title: string;
  desc: string;
  progress: number;
  goal: number;
  unit: string;
  color: string;
  progressColor: string;
}

const missions: Mission[] = [
  {
    id: 'mission-watch',
    emoji: '👁️',
    title: "Watch a whale's journey",
    desc: 'Observe the humpback whale for 2 minutes without interrupting.',
    progress: 87,
    goal: 120,
    unit: 'seconds',
    color: 'border-kid-dolphin/30 bg-kid-dolphin/5',
    progressColor: 'bg-kid-dolphin',
  },
  {
    id: 'mission-count',
    emoji: '🐟',
    title: 'Count the fish school',
    desc: 'How many sardines can you spot passing by?',
    progress: 34,
    goal: 50,
    unit: 'fish',
    color: 'border-kid-fish/30 bg-kid-fish/5',
    progressColor: 'bg-kid-fish',
  },
  {
    id: 'mission-ice',
    emoji: '🧊',
    title: 'Notice the ice change',
    desc: 'Watch the ice shelf heartbeat and notice how it changes shape.',
    progress: 1,
    goal: 3,
    unit: 'changes',
    color: 'border-accent/30 bg-accent/5',
    progressColor: 'bg-accent',
  },
];

export default function CareMissions() {
  const [completed, setCompleted] = useState<string[]>([]);

  return (
    <div className="bg-card/40 border border-border/50 rounded-2xl p-5 backdrop-blur-sm h-full">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg">🌊</span>
        <div>
          <h2 className="text-sm font-semibold text-foreground">Care Missions</h2>
          <p className="text-[10px] text-kid-blue/70">Observe only — never disturb</p>
        </div>
      </div>

      <div className="space-y-4">
        {missions.map((m) => {
          const pct = Math.round((m.progress / m.goal) * 100);
          const done = pct >= 100 || completed.includes(m.id);
          return (
            <div key={m.id} className={`rounded-xl border p-4 transition-all ${done ? 'border-positive/30 bg-positive/5' : m.color}`}>
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">{done ? '✅' : m.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold mb-0.5 ${done ? 'text-positive' : 'text-foreground'}`}>{m.title}</p>
                  <p className="text-[10px] text-muted-foreground leading-relaxed mb-2">{m.desc}</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${done ? 'bg-positive' : m.progressColor}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-muted-foreground font-tabular flex-shrink-0">
                      {done ? `${m.goal}/${m.goal}` : `${m.progress}/${m.goal}`} {m.unit}
                    </span>
                  </div>
                </div>
              </div>
              {!done && pct >= 80 && (
                <button
                  onClick={() => setCompleted((p) => [...p, m.id])}
                  className="mt-2 w-full py-1 text-[10px] font-semibold text-positive bg-positive/10 border border-positive/20 rounded-lg hover:bg-positive/20 transition-colors"
                >
                  Mark as complete ✓
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 rounded-xl bg-muted/20 border border-border/30 text-center">
        <p className="text-[10px] text-muted-foreground">
          No scores · No competition · Only care and observation 🌊
        </p>
      </div>
    </div>
  );
}